import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import apiClient from "@/utils/apiClient";

type EtatPage = "verif" | "pret" | "succes" | "invalide";

function validerMotDePasse(motDePasse: string): string | null {
  if (motDePasse.length < 8) {
    return "Le mot de passe doit contenir au moins 8 caractères";
  }

  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
  if (!regex.test(motDePasse)) {
    return "Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre";
  }

  return null;
}

export default function AccountSetup() {
  const [searchParams] = useSearchParams();
  const token = useMemo(
    () => searchParams.get("token")?.trim() || "",
    [searchParams],
  );

  const [etat, setEtat] = useState<EtatPage>("verif");
  const [email, setEmail] = useState("");
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let annule = false;

    const verifier = async () => {
      if (!token) {
        setEtat("invalide");
        setError("Lien d'activation invalide.");
        return;
      }

      setEtat("verif");
      setError("");

      try {
        const response = await apiClient.get<{
          email: string;
          expiresAt: string;
        }>("/auth/account-setup/validate", {
          params: { token },
        });

        if (annule) return;

        setEmail(response.data.email);
        setExpiresAt(response.data.expiresAt);
        setEtat("pret");
      } catch (err: any) {
        if (annule) return;
        setEtat("invalide");
        setError(
          err.response?.data?.error || "Lien d'activation invalide ou expiré",
        );
      }
    };

    void verifier();

    return () => {
      annule = true;
    };
  }, [token]);

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Lien d'activation invalide");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError("Tous les champs sont requis");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    const erreurMotDePasse = validerMotDePasse(newPassword);
    if (erreurMotDePasse) {
      setError(erreurMotDePasse);
      return;
    }

    setLoading(true);
    try {
      await apiClient.post("/auth/account-setup/confirm", {
        token,
        newPassword,
      });
      setEtat("succes");
    } catch (err: any) {
      setError(err.response?.data?.error || "Impossible d'activer le compte");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <img
          src="/assets/logo.png"
          alt="PIXAtwin Logo"
          className="login-logo"
        />

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {etat === "verif" && (
          <>
            <h1 className="text-lg font-semibold">Activation du compte</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Vérification du lien en cours...
            </p>
          </>
        )}

        {etat === "invalide" && (
          <>
            <h1 className="text-lg font-semibold">Lien invalide</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Le lien est invalide, expiré ou a déjà été utilisé.
            </p>
            <div className="mt-6">
              <Button asChild className="w-full">
                <Link to="/login">Retour à la connexion</Link>
              </Button>
            </div>
          </>
        )}

        {etat === "pret" && (
          <>
            <h1 className="text-lg font-semibold">
              Choisir votre mot de passe
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Activez votre compte pour <strong>{email}</strong>.
            </p>
            {expiresAt ? (
              <p className="text-xs text-slate-500 mt-2">
                Lien valable jusqu'au{" "}
                {new Date(expiresAt).toLocaleString("fr-CH", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
                .
              </p>
            ) : null}

            <form onSubmit={soumettre} className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Au moins 8 caractères"
                  autoComplete="new-password"
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">
                  Confirmer le mot de passe
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Répétez le mot de passe"
                  autoComplete="new-password"
                  disabled={loading}
                  required
                />
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "Activation en cours..." : "Activer mon compte"}
              </Button>
            </form>
          </>
        )}

        {etat === "succes" && (
          <>
            <h1 className="text-lg font-semibold">Compte activé</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Votre mot de passe a été enregistré. Vous pouvez maintenant vous
              connecter.
            </p>
            <div className="mt-6">
              <Button asChild className="w-full">
                <Link to="/login">Aller à la connexion</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
